import React from 'react';
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import Login from './pages/Login';
import ChangePassword from './pages/ChangePassword';
import HomePage from './apps/home/HomePage';
import HomeAdminPanel from './apps/home/HomeAdminPanel';
import HelpPage from './apps/help/HelpPage';
import Dashboard from './apps/service-requests/pages/Dashboard';
import Admin from './apps/service-requests/pages/Admin';
import CreateRequest from './apps/service-requests/pages/CreateRequest';
import RequestDetails from './apps/service-requests/pages/RequestDetails';
import EditRequest from './apps/service-requests/pages/EditRequest';
import RaiseTheSailLayout from './apps/raise-the-sail/RaiseTheSailLayout';
import ChecklistHome from './apps/raise-the-sail/pages/ChecklistHome';
import ChecklistReports from './apps/raise-the-sail/pages/ChecklistReports';
import ChecklistAdmin from './apps/raise-the-sail/pages/ChecklistAdmin';
import ChecklistInspectionForm from './apps/raise-the-sail/pages/ChecklistInspectionForm';
import RiskLayout from './apps/risk/RiskLayout';
import RiskHome from './apps/risk/pages/RiskHome';
import RiskReports from './apps/risk/pages/RiskReports';
import RiskAdmin from './apps/risk/pages/RiskAdmin';
import RiskInspectionForm from './apps/risk/pages/RiskInspectionForm';
import HomesiteLayout from './apps/homesite-inspections/HomesiteLayout';
import HomesiteHome from './apps/homesite-inspections/pages/HomesiteHome';
import HomesiteReports from './apps/homesite-inspections/pages/HomesiteReports';
import HomesiteAdmin from './apps/homesite-inspections/pages/HomesiteAdmin';
import HomesiteInspectionForm from './apps/homesite-inspections/pages/HomesiteInspectionForm';
import RulesViolationLayout from './apps/rules-violation/RulesViolationLayout';
import RulesViolationHome from './apps/rules-violation/pages/RulesViolationHome';
import RulesViolationInspection from './apps/rules-violation/pages/RulesViolationInspection';
import RulesViolationReports from './apps/rules-violation/pages/RulesViolationReports';
import RulesViolationReportView from './apps/rules-violation/pages/RulesViolationReportView';
import RulesViolationAdmin from './apps/rules-violation/pages/RulesViolationAdmin';
import HurricaneLayout from './apps/hurricane/HurricaneLayout';
import HurricaneHome from './apps/hurricane/pages/HurricaneHome';
import HurricaneReport from './apps/hurricane/pages/HurricaneReport';
import HurricaneReports from './apps/hurricane/pages/HurricaneReports';
import HurricaneReportView from './apps/hurricane/pages/HurricaneReportView';
import HurricaneAdmin from './apps/hurricane/pages/HurricaneAdmin';
import InventoryLayout from './apps/inventory/InventoryLayout';
import InventoryHome from './apps/inventory/pages/InventoryHome';
import InventoryAdmin from './apps/inventory/pages/InventoryAdmin';
import ProtectedRoute from './components/ProtectedRoute';
import SubAppRoute from './components/SubAppRoute';
import FunctionRoute from './components/FunctionRoute';
import CatchAllRedirect from './components/CatchAllRedirect';
import { LegacyRequestDetailRedirect, LegacyRequestEditRedirect } from './components/LegacyRouteRedirects';
import { ROUTES, rulesViolationReport } from './constants/routes';
import { SUB_APPS } from './utils/rbac';

function RulesViolationInspectRedirect() {
  const { id } = useParams();
  return <Navigate to={rulesViolationReport(id)} replace />;
}

const routeTransition = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -10 },
};

function AppRoutes() {
  const location = useLocation();
  const reduceMotion = useReducedMotion();

  const routes = (
    <Routes location={location}>
      <Route path={ROUTES.LOGIN} element={<Login />} />
      <Route
        path={ROUTES.CHANGE_PASSWORD}
        element={(
          <ProtectedRoute>
            <ChangePassword />
          </ProtectedRoute>
        )}
      />

      <Route path={ROUTES.HOME} element={<ProtectedRoute><HomePage /></ProtectedRoute>} />
      <Route path={ROUTES.HELP} element={<ProtectedRoute><HelpPage /></ProtectedRoute>} />
      <Route
        path={ROUTES.HOME_ADMIN}
        element={(
          <ProtectedRoute>
            <FunctionRoute appKey={SUB_APPS.HOME_ADMIN} fnKey="panel">
              <HomeAdminPanel />
            </FunctionRoute>
          </ProtectedRoute>
        )}
      />

      <Route
        path={ROUTES.SERVICE_REQUESTS}
        element={(
          <ProtectedRoute>
            <SubAppRoute appKey={SUB_APPS.SERVICE_REQUESTS}>
              <Dashboard />
            </SubAppRoute>
          </ProtectedRoute>
        )}
      />
      <Route
        path={ROUTES.SERVICE_REQUESTS_NEW}
        element={(
          <ProtectedRoute>
            <SubAppRoute appKey={SUB_APPS.SERVICE_REQUESTS}>
              <CreateRequest />
            </SubAppRoute>
          </ProtectedRoute>
        )}
      />
      <Route
        path="/service-requests/:id"
        element={(
          <ProtectedRoute>
            <SubAppRoute appKey={SUB_APPS.SERVICE_REQUESTS}>
              <RequestDetails />
            </SubAppRoute>
          </ProtectedRoute>
        )}
      />
      <Route
        path="/service-requests/:id/edit"
        element={(
          <ProtectedRoute>
            <SubAppRoute appKey={SUB_APPS.SERVICE_REQUESTS}>
              <EditRequest />
            </SubAppRoute>
          </ProtectedRoute>
        )}
      />

      <Route
        path={ROUTES.ADMIN}
        element={(
          <ProtectedRoute>
            <FunctionRoute appKey={SUB_APPS.HOME_ADMIN} fnKey="panel">
              <Admin />
            </FunctionRoute>
          </ProtectedRoute>
        )}
      />

      <Route
        path={ROUTES.RAISE_THE_SAIL}
        element={(
          <ProtectedRoute>
            <SubAppRoute appKey={SUB_APPS.RAISE_THE_SAIL}>
              <RaiseTheSailLayout />
            </SubAppRoute>
          </ProtectedRoute>
        )}
      >
        <Route index element={<ChecklistHome />} />
        <Route path="reports" element={<ChecklistReports />} />
        <Route
          path="admin"
          element={(
            <FunctionRoute appKey={SUB_APPS.RAISE_THE_SAIL} fnKey="admin">
              <ChecklistAdmin />
            </FunctionRoute>
          )}
        />
        <Route path="inspect/:id" element={<ChecklistInspectionForm />} />
      </Route>

      <Route
        path={ROUTES.RISK}
        element={(
          <ProtectedRoute>
            <SubAppRoute appKey={SUB_APPS.RISK_CHECKLIST}>
              <RiskLayout />
            </SubAppRoute>
          </ProtectedRoute>
        )}
      >
        <Route index element={<RiskHome />} />
        <Route path="reports" element={<RiskReports />} />
        <Route
          path="admin"
          element={(
            <FunctionRoute appKey={SUB_APPS.RISK_CHECKLIST} fnKey="admin">
              <RiskAdmin />
            </FunctionRoute>
          )}
        />
        <Route path="inspect/:id" element={<RiskInspectionForm />} />
      </Route>

      <Route
        path={ROUTES.HOMESITE}
        element={(
          <ProtectedRoute>
            <SubAppRoute appKey={SUB_APPS.HOMESITE}>
              <HomesiteLayout />
            </SubAppRoute>
          </ProtectedRoute>
        )}
      >
        <Route index element={<HomesiteHome />} />
        <Route path="reports" element={<HomesiteReports />} />
        <Route
          path="admin"
          element={(
            <FunctionRoute appKey={SUB_APPS.HOMESITE} fnKey="admin">
              <HomesiteAdmin />
            </FunctionRoute>
          )}
        />
        <Route path="inspect/:id" element={<HomesiteInspectionForm />} />
      </Route>

      <Route
        path={ROUTES.RULES_VIOLATION}
        element={(
          <ProtectedRoute>
            <SubAppRoute appKey={SUB_APPS.RULES_VIOLATION}>
              <RulesViolationLayout />
            </SubAppRoute>
          </ProtectedRoute>
        )}
      >
        <Route index element={<RulesViolationHome />} />
        <Route path="violation/:id" element={<RulesViolationInspection />} />
        <Route path="inspect/:id" element={<RulesViolationInspectRedirect />} />
        <Route
          path="reports"
          element={(
            <FunctionRoute appKey={SUB_APPS.RULES_VIOLATION} fnKey="reports">
              <RulesViolationReports />
            </FunctionRoute>
          )}
        />
        <Route
          path="reports/item/:itemId"
          element={(
            <FunctionRoute appKey={SUB_APPS.RULES_VIOLATION} fnKey="reports">
              <RulesViolationReportView />
            </FunctionRoute>
          )}
        />
        <Route
          path="admin"
          element={(
            <FunctionRoute appKey={SUB_APPS.RULES_VIOLATION} fnKey="admin">
              <RulesViolationAdmin />
            </FunctionRoute>
          )}
        />
      </Route>

      <Route
        path={ROUTES.HURRICANE}
        element={(
          <ProtectedRoute>
            <SubAppRoute appKey={SUB_APPS.HURRICANE}>
              <HurricaneLayout />
            </SubAppRoute>
          </ProtectedRoute>
        )}
      >
        <Route index element={<HurricaneHome />} />
        <Route path="report/:id" element={<HurricaneReport />} />
        <Route
          path="reports"
          element={(
            <FunctionRoute appKey={SUB_APPS.HURRICANE} fnKey="reports">
              <HurricaneReports />
            </FunctionRoute>
          )}
        />
        <Route
          path="reports/view/:id"
          element={(
            <FunctionRoute appKey={SUB_APPS.HURRICANE} fnKey="reports">
              <HurricaneReportView />
            </FunctionRoute>
          )}
        />
        <Route
          path="admin"
          element={(
            <FunctionRoute appKey={SUB_APPS.HURRICANE} fnKey="admin">
              <HurricaneAdmin />
            </FunctionRoute>
          )}
        />
      </Route>

      <Route
        path={ROUTES.INVENTORY}
        element={(
          <ProtectedRoute>
            <SubAppRoute appKey={SUB_APPS.INVENTORY}>
              <InventoryLayout />
            </SubAppRoute>
          </ProtectedRoute>
        )}
      >
        <Route index element={<InventoryHome />} />
        <Route
          path="admin"
          element={(
            <FunctionRoute appKey={SUB_APPS.INVENTORY} fnKey="admin">
              <InventoryAdmin />
            </FunctionRoute>
          )}
        />
      </Route>

      <Route path="/dashboard" element={<Navigate to={ROUTES.SERVICE_REQUESTS} replace />} />
      <Route path="/requests/new" element={<Navigate to={ROUTES.SERVICE_REQUESTS_NEW} replace />} />
      <Route path="/requests/:id" element={<LegacyRequestDetailRedirect />} />
      <Route path="/requests/:id/edit" element={<LegacyRequestEditRedirect />} />

      <Route path="*" element={<CatchAllRedirect />} />
    </Routes>
  );

  if (reduceMotion) {
    return <div className="app-route-layer">{routes}</div>;
  }

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={location.pathname}
        className="app-route-layer"
        initial={routeTransition.initial}
        animate={routeTransition.animate}
        exit={routeTransition.exit}
        transition={{ duration: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
      >
        {routes}
      </motion.div>
    </AnimatePresence>
  );
}

function App() {
  return <AppRoutes />;
}

export default App;
